import React, { createContext, useContext, useState } from 'react';
import en from './i18n/en.json';
import ta from './i18n/ta.json';

type Language = 'en' | 'ta';

interface LanguageContextType {
    language: Language;
    setLanguage: (lang: Language) => void;
    t: (key: string) => string;
    getArray: (key: string) => any[];
    getObject: (key: string) => any;
}

const translations: any = { en, ta };

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [language, setLanguageState] = useState<Language>(() => {
        const saved = localStorage.getItem('language');
        if (saved === 'en' || saved === 'ta') return saved as Language;
        return 'en';
    });

    const setLanguage = (lang: Language) => {
        setLanguageState(lang);
        localStorage.setItem('language', lang);
    };

    const getNestedValue = (obj: any, keys: string[]): any => {
        let value = obj;
        for (const key of keys) {
            if (value && typeof value === 'object' && key in value) {
                value = value[key];
            } else {
                return undefined;
            }
        }
        return value;
    };

    const t = (key: string): string => {
        const keys = key.split('.');
        const value = getNestedValue(translations[language], keys);

        if (typeof value === 'string') {
            return value;
        }

        return key;
    };

    const getArray = (key: string): any[] => {
        const keys = key.split('.');
        const value = getNestedValue(translations[language], keys);

        if (Array.isArray(value)) {
            return value;
        }

        if (typeof value === 'string') {
            return value.split('|').map((item: string, index: number) => ({
                id: index + 1,
                text: item.trim(),
                name: item.trim(),
                mood: 'romantic'
            }));
        }

        return [];
    };

    const getObject = (key: string): any => {
        const keys = key.split('.');
        const value = getNestedValue(translations[language], keys);

        if (value && typeof value === 'object' && !Array.isArray(value)) {
            return value;
        }

        return {};
    };

    return (
        <LanguageContext.Provider value={{
            language,
            setLanguage,
            t,
            getArray,
            getObject
        }}>
            {children}
        </LanguageContext.Provider>
    );
};

export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
};
